<script lang="ts">
    import { isEmpty, isEqual } from 'lodash-es';
    import { onMount, createEventDispatcher } from 'svelte';
    import { stopPropagation } from 'svelte/legacy';

    type Item = string | number | { label: string; value: unknown };

    export interface SelectProps {
        /**
         * Indicator wherther to allow multiple selection
         */
        multiple?: boolean;
        /**
         * Indicator wherther the element should have 100% width
         */
        fullWidth?: boolean;
        /**
         * Indicator wherther the select should be disabled
         */
        disabled?: boolean;
        /**
         * Selectable options
         */
        items?: Item[];
        /**
         * Selected value or values if multiple
         */
        value?: Item[];
        /**
         * Value placeholder if none is selected yet
         */
        placeholder?: string;
    }

    let {
        multiple = false,
        fullWidth = false,
        disabled = false,
        items = [],
        value = [],
        placeholder = 'Select',
    }: SelectProps = $props();

    let elementSelect = $state<HTMLDivElement | undefined>();
    let elementInput = $state<HTMLButtonElement | undefined>();
    let elementDropdown = $state<HTMLUListElement | undefined>();
    const dispatch = createEventDispatcher();

    onMount(() => {
        resizeDropdown();
    });

    function resizeDropdown() {
        if (!elementDropdown || !elementInput) return;
        // move dropdown right under input
        const { width, left } = elementInput.getBoundingClientRect();
        const { left: dropdownLeft } = elementDropdown.getBoundingClientRect();
        elementDropdown.style.marginLeft = `${Math.abs(dropdownLeft - left)}px`;
    }

    function handleClickOut(e: MouseEvent) {
        if (!elementSelect) {
            document.removeEventListener('click', handleClickOut);
            return;
        }

        if (!elementSelect.contains(e.target as Node | null)) {
            close();
        }
    }

    function setValue(newValue: Item) {
        value = multiple
            ? value.includes(newValue)
                ? value.filter(v => v !== newValue)
                : [...value, newValue]
            : [newValue];

        if (!multiple && elementInput) {
            elementInput.focus();
            close();
        }

        /**
         * Change value event.
         *
         * @event change
         * @property {Item | Item[]} value new value set
         */
        dispatch('change', multiple ? value : value[0]);
    }

    function handleItemKeyDown(e: KeyboardEvent) {
        e.preventDefault();
        if (!e.target) return;
        const target = e.target as HTMLElement;

        if (e.key === 'ArrowUp' && target.previousElementSibling) {
            (target.previousElementSibling as HTMLElement).focus();
        } else if (e.key === 'ArrowDown' && target.nextElementSibling) {
            (target.nextElementSibling as HTMLElement).focus();
        } else if (e.key === 'Escape') {
            close();
        } else if (e.key === 'Enter' || e.key === ' ') {
            const index = target.getAttribute('tabindex');
            index && setValue(items[parseInt(index, 10)]);
        }
    }

    function handleToggleKeyPress(e: KeyboardEvent) {
        e.preventDefault();

        if (e.key === 'Escape') {
            close();
        } else if (e.key === 'Enter' || e.key === ' ') {
            open();
        }
    }

    function toggle(e: MouseEvent) {
        e.stopPropagation();
        if (!elementDropdown) return;
        const isOpen = elementDropdown.getAttribute('aria-expanded') === 'true';
        isOpen ? close() : open();
    }

    function open() {
        if (!elementDropdown || !elementInput) return;
        elementInput.classList.add('active');
        elementDropdown.setAttribute('aria-expanded', 'true');
        elementDropdown.classList.add('expanded');
        /**
         * Open dropdown event.
         *
         * @event open
         */
        dispatch('open');
        document.addEventListener('click', handleClickOut);
    }

    function close() {
        if (!elementDropdown || !elementInput) return;
        elementInput.classList.remove('active');
        elementDropdown.setAttribute('aria-expanded', 'false');
        elementDropdown.classList.remove('expanded');
        /**
         * Close dropdown event.
         *
         * @event close
         */
        dispatch('close');
        document.removeEventListener('click', handleClickOut);
    }

    function getLabel(value: Item) {
        return typeof value === 'object' ? value.label : value;
    }

    function isSelected(item: Item) {
        return multiple ? value.includes(item) : isEqual(item, value[0]);
    }
    let inputValueLabel = $derived(
        isEmpty(value)
            ? placeholder
            : multiple
              ? value.map(getLabel).join(', ')
              : getLabel(value[0]),
    );
</script>

<div class="select" class:disabled bind:this={elementSelect} aria-multiselectable={multiple}>
    <button
        class="input"
        class:full-width={fullWidth}
        class:active={false}
        bind:this={elementInput}
        type="button"
        aria-haspopup="true"
        onkeydown={handleToggleKeyPress}
        onclick={toggle}
    >
        {inputValueLabel}
    </button>
    <ul
        class="dropdown"
        class:expanded={false}
        bind:this={elementDropdown}
        role="listbox"
        aria-expanded="false"
    >
        {#each items as item, idx}
            <!--
                @slot dropdown menu item
                @binding {object} item item in the dropdown list
                @binding {number} idx index of item
                @binding {boolean} selected indicator if the item is selected
            -->
            <slot name="item" slot="item" {item} {idx} selected={isSelected(item)}>
                <li
                    role="option"
                    class:selected={isSelected(item)}
                    onkeydown={handleItemKeyDown}
                    onclick={stopPropagation(() => setValue(item))}
                    tabindex={idx}
                >
                    {getLabel(item)}
                </li>
            </slot>
        {/each}
    </ul>
</div>

<style lang="scss" scoped>
    .select {
        position: relative;
        cursor: pointer;
        display: block;

        &.disabled {
            pointer-events: none;
            opacity: 0.8;
            cursor: default;
        }

        .input {
            appearance: none;
            padding: 2px 32px 2px 2px;
            background: none;
            border: 1px solid #555;
            border-radius: 3px;
            text-align: left;
            position: relative;
            max-width: 300px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            cursor: pointer;
            outline: none;

            &.full-width {
                width: 100%;
            }

            &::after {
                content: '';
                border-width: 6px;
                border-radius: 3px;
                border-style: solid;
                border-color: transparent;
                border-top-color: inherit;
                position: absolute;
                right: 8px;
                top: calc(50% + 3px);
                transform: translateY(-50%);
            }

            &.active::after {
                border-color: transparent;
                border-bottom-color: inherit;
                top: calc(50% - 3px);
            }
        }

        &[aria-multiselectable='true'] {
            li::before {
                content: '☐';
                margin-right: 0.3rem;
            }

            li.selected::before {
                content: '✓';
            }
        }

        .dropdown {
            position: fixed;
            background: #fff;
            box-shadow: 0 0 5px rgba(120, 120, 120, 0.65);
            list-style: none;
            padding: 2px;
            margin: 0;
            min-width: 80px;
            border-radius: 3px;
            z-index: 10;
            visibility: hidden;
            overflow: auto;
            max-height: 200px;

            &.expanded {
                visibility: visible;
            }

            > li {
                display: block;
                align-items: center;
                padding: 3px 4px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                font-size: 0.8rem;

                &:hover,
                &:focus {
                    outline: none;
                    background: #f2f2f2;
                }

                &.selected {
                    font-weight: bold;
                    background-color: #f2f2f2;
                }
            }
        }
    }
</style>
